<template>
  <div class="property-tab">
    <div class="appearance-grid">

      <!-- State Body -->
      <span class="appearance-label">Body:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-state-fill-color" :color="stateColor" size="small" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change State Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="stateColor" class="overflow-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
      </div>

      <!-- State Border -->
      <span class="appearance-label">Border:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-state-border-color" size="small" :color="lineColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Border Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="lineColor" class="overflow-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
          v-model="borderWidth"
          class="ml-2"
          hide-details
          min="0" max="10" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"
        ></v-slider>
        <span class="slider-value">{{ borderWidth }}</span>
      </div>

      <!-- State Name -->
      <span class="appearance-label">Name:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-state-name-color" size="small" :color="textColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Name Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="textColor" elevation="15" class="overflow-hidden" mode="rgba" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
          v-model="fontSize"
          class="ml-2"
          hide-details
          min="6" max="26" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"
        ></v-slider>
        <span class="slider-value">{{ fontSize }}</span>
      </div>

      <!-- Region Layout -->
      <span class="appearance-label">Layout:</span>
      <v-btn-toggle v-model="layoutDirection" mandatory density="compact" class="flex-toggle" color="font">
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn value="horizontal" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-arrow-left-right</v-icon>
            </v-btn>
          </template>
          <span>Horizontal</span>
        </v-tooltip>
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn value="vertical" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-arrow-up-down</v-icon>
            </v-btn>
          </template>
          <span>Vertical</span>
        </v-tooltip>
      </v-btn-toggle>

      <!-- Show -->
      <span class="appearance-label">Show:</span>
      <v-btn-toggle v-model="showSpecification" mandatory density="compact" class="flex-toggle" color="font">
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn :value="true" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-code-braces</v-icon>
            </v-btn>
          </template>
          <span>Expression</span>
        </v-tooltip>
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn :value="false" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-text-box</v-icon>
            </v-btn>
          </template>
          <span>Docs</span>
        </v-tooltip>
      </v-btn-toggle>

    </div>

    <div class="appearance-footer">
      <v-btn size="small" color="primary" :disabled="PreferenceStore.INSTANCE.isReadOnly" @click="resetDefaults()">Reset Defaults</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { State } from "../mvc/state";
import RappidContext from "../rappid/rappid-context";
import { computed, inject, onBeforeUnmount, ref } from "vue";
import { PreferenceStore } from "./../services/preference-store";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const colorPicking = ref<boolean>(false);

const compProps = defineProps<{
  model: State;
}>();

const emit = defineEmits(["notifyChange"]);

onBeforeUnmount(() => {
  if (colorPicking.value) {
    rappidContext.commandManager.storeBatchCommand();
  }
});

function colorPickerHandler(value: boolean) {
  colorPicking.value = value;
  if (value) {
    startBatchCommand();
  } else {
    stopBatchCommand();
  }
}

function startBatchCommand() {
  rappidContext.commandManager.initBatchCommand();
}

function stopBatchCommand() {
  rappidContext.commandManager.storeBatchCommand();
}

function resetDefaults() {
  startBatchCommand();
  compProps.model.resetDefaults();
  stopBatchCommand();
}

const layoutDirection = computed({
  get() {
    return compProps.model.getLayoutDirection();
  },
  set(value: "horizontal" | "vertical") {
    startBatchCommand();
    compProps.model.setLayoutDirection(value);
    emit("notifyChange", compProps.model);
    stopBatchCommand();
  },
});

const showSpecification = computed({
  get() {
    return compProps.model.getShowSpecification();
  },
  set(value: boolean) {
    startBatchCommand();
    compProps.model.setShowSpecification(value);
    emit("notifyChange", compProps.model);
    stopBatchCommand();
  },
});

const stateColor = computed({
  get(): string {
    return compProps.model.getFillColor();
  },
  set(color: string) {
    compProps.model.setFillColor(color);
  },
});

const lineColor = computed({
  get(): string {
    return compProps.model.getBorderColor();
  },
  set(color: string) {
    compProps.model.setBorderColor(color);
  },
});

const textColor = computed({
  get(): string {
    return compProps.model.getNameColor();
  },
  set(color: string) {
    compProps.model.setNameColor(color);
  },
});

const fontSize = computed({
  get(): number {
    return compProps.model.getNameFontSize();
  },
  set(size: number) {
    compProps.model.setNameFontSize(size);
  },
});

const borderWidth = computed({
  get(): number {
    return compProps.model.getBorderWidth();
  },
  set(width: number) {
    compProps.model.setBorderWidth(width);
  },
});
</script>
